/**
 * GuestAttachmentsSection Component
 * File attachments management for guest project view
 */
import React, { useRef, useState } from 'react';
import { Card, Button, Spinner, ListGroup, Alert } from 'react-bootstrap';
import { Paperclip, Plus, Trash2, Download, FileText } from 'lucide-react';
import { Attachment, isImageAttachment } from '../../../types/attachments';
import ConfirmationModal from '../../../Components/ConfirmationModal';

interface GuestAttachmentsSectionProps {
    attachments: { [key: string]: Attachment } | undefined;
    externalUserId: string;
    onUploadAttachment: (file: File) => Promise<void>;
    onDeleteAttachment: (attachmentKey: string) => Promise<void>;
    onGetDownloadUrl: (storagePath: string) => Promise<string>;
    submitting: boolean;
    canDelete: (authorId: string) => boolean;
    /** Completed project: everything is shown, nothing can be added or changed. */
    readOnly?: boolean;
}

const GuestAttachmentsSection: React.FC<GuestAttachmentsSectionProps> = ({
    attachments,
    externalUserId,
    onUploadAttachment,
    onDeleteAttachment,
    onGetDownloadUrl,
    submitting,
    canDelete,
    readOnly = false,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; attachmentKey: string | null }>({
        show: false,
        attachmentKey: null,
    });
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const attachmentsArray = attachments
        ? Object.entries(attachments).map(([key, attachment]) => ({ ...attachment, key }))
        : [];

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            setUploadError(null);
            try {
                await onUploadAttachment(files[0]);
            } catch (err) {
                console.error('Error uploading attachment:', err);
                const errorMessage = err instanceof Error ? err.message : 'Kunde inte ladda upp fil';
                setUploadError(errorMessage);
            }
        }
        // Reset input so same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDeleteClick = (attachmentKey: string) => {
        setDeleteConfirm({ show: true, attachmentKey });
    };

    const handleDeleteConfirm = async () => {
        if (deleteConfirm.attachmentKey) {
            await onDeleteAttachment(deleteConfirm.attachmentKey);
        }
        setDeleteConfirm({ show: false, attachmentKey: null });
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm({ show: false, attachmentKey: null });
    };

    const handleDownload = async (attachment: Attachment & { key: string }) => {
        setDownloadingKey(attachment.key);
        setDownloadError(null);
        try {
            const url = await onGetDownloadUrl(attachment.storagePath);
            // Open in new tab or trigger download
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.download = getFileName(attachment);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error downloading file:', err);
            setDownloadError('Kunde inte ladda ner filen');
        } finally {
            setDownloadingKey(null);
        }
    };

    /**
     * Get author ID from attachment (handles both image and file attachments)
     */
    const getAuthor = (attachment: Attachment): string => {
        if (isImageAttachment(attachment)) {
            return attachment.author;
        }
        return attachment.uploadedBy;
    };

    /**
     * Get file name from attachment
     */
    const getFileName = (attachment: Attachment): string => {
        if (isImageAttachment(attachment)) {
            return attachment.fileName || 'bild';
        }
        return attachment.fileName;
    };

    /**
     * Get file size from attachment (handles both types)
     */
    const getFileSize = (attachment: Attachment): number => {
        if (isImageAttachment(attachment)) {
            return attachment.fileSize || 0;
        }
        return attachment.fileSize;
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <>
            <Card className="guest-attachments-section mb-4">
                <Card.Header className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <Paperclip size={18} />
                        <span>Bilagor</span>
                        {attachmentsArray.length > 0 && (
                            <span className="badge bg-secondary">{attachmentsArray.length}</span>
                        )}
                    </div>
                    {!readOnly && (
                        <label className="btn btn-outline-primary btn-sm mb-0">
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="d-none"
                                onChange={handleFileChange}
                                disabled={submitting}
                            />
                            <Plus size={16} className="me-1" />
                            Lägg till
                        </label>
                    )}
                </Card.Header>
                <Card.Body>
                    {uploadError && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => setUploadError(null)}
                            className="mb-3"
                        >
                            {uploadError}
                        </Alert>
                    )}
                    {downloadError && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => setDownloadError(null)}
                            className="mb-3"
                        >
                            {downloadError}
                        </Alert>
                    )}

                    {attachmentsArray.length === 0 ? (
                        <p className="text-muted text-center mb-0">
                            Inga bilagor ännu
                        </p>
                    ) : (
                        <ListGroup variant="flush">
                            {attachmentsArray.map((attachment) => (
                                <ListGroup.Item
                                    key={attachment.key}
                                    className="d-flex align-items-center justify-content-between"
                                >
                                    <div className="d-flex align-items-center gap-2 flex-grow-1 min-width-0">
                                        <FileText size={20} className="text-muted flex-shrink-0" />
                                        <div className="min-width-0">
                                            <div className="text-truncate" title={getFileName(attachment)}>
                                                {getFileName(attachment)}
                                            </div>
                                            <small className="text-muted">
                                                {formatFileSize(getFileSize(attachment))}
                                            </small>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2 flex-shrink-0">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleDownload(attachment)}
                                            disabled={submitting || downloadingKey === attachment.key}
                                        >
                                            {downloadingKey === attachment.key ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                <Download size={14} />
                                            )}
                                        </Button>
                                        {!readOnly && canDelete(getAuthor(attachment)) && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeleteClick(attachment.key)}
                                                disabled={submitting}
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        )}
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>

            <ConfirmationModal
                show={deleteConfirm.show}
                title="Ta bort bilaga"
                message="Vill du ta bort denna fil?"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={submitting}
            />
        </>
    );
};

export default GuestAttachmentsSection;
